import { logger } from '@adornis/base/logging.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import {
  type BoundingBoxContentType,
  LEGACY_CONTENT_TYPE,
  normalizeContentType,
} from '../../shared/bounding-box-content.js';
import { findLanguageUseSchemesForTask } from '../../shared/grammar-rules-resolution.js';
import { loadAndAssert } from '../authz/access.js';
import { Exam } from '../exam.js';
import { Submission } from '../submission.js';
import { Teacher } from '../teacher.js';
import { cropAndSaveRegion } from '../utils/crop-image.js';
import { aggregateOCRFromBoundingBoxes } from '../utils/exam-utils.js';
import { joinTaskRefSegments } from '../utils/task-ref.js';
import { runOCROnImage } from './agents/extraction-modules/ocr-utils.js';
import { OcrExtractionType } from './agents/extraction-modules/ocrExtractionTypes.js';
import { TaskType, taskTypeBehaviours } from './task-types/task-main.js';

/**
 * Bag-of-words difference, order-insensitive: the larger of (word-instances added, word-instances
 * removed) between old and new text. A one-word substitution ("cat"→"dog") counts as 1 — matching what
 * the teacher actually did — rather than 2 (a plain added+removed sum would double-count every
 * substitution, which reads as confusing/wrong against a cutoff the teacher set expecting it to mean
 * roughly "this many words edited"). Inserting or deleting N words with nothing else changed still
 * counts as N either way. Used to gate Teacher.grammarPreferences.{content,languageUseRegrade}
 * WordCutoff against SubmissionTask.pending{Content,LanguageUse}WordDelta.
 */
function countChangedWords(oldText: string, newText: string): number {
  const tally = (text: string) => {
    const counts = new Map<string, number>();
    for (const w of text.trim().split(/\s+/).filter(Boolean)) counts.set(w, (counts.get(w) ?? 0) + 1);
    return counts;
  };
  const oldCounts = tally(oldText ?? '');
  const newCounts = tally(newText ?? '');
  let added = 0;
  let removed = 0;
  for (const w of new Set([...oldCounts.keys(), ...newCounts.keys()])) {
    const delta = (newCounts.get(w) ?? 0) - (oldCounts.get(w) ?? 0);
    if (delta > 0) added += delta;
    else removed -= delta;
  }
  return Math.max(added, removed);
}

@Entity()
export class UpdateSubmissionTaskResult extends AdornisEntity {
  static override _class = 'UpdateSubmissionTaskResult';

  @Field(() => Boolean)
  success!: boolean;

  @Field(() => String)
  message!: string;
}

export const updateSubmissionTaskMutation = registerMutation({
  operationName: 'updateSubmissionTaskMutation',
  type: () => UpdateSubmissionTaskResult,
  params: [
    { name: 'submissionId', type: () => String },
    { name: 'mainTaskId', type: () => String },
    { name: 'subTaskId', type: () => String },
    { name: 'boundingBoxIndex', type: () => Float },
    { name: 'extractedText', type: () => String },
    { name: 'score', type: () => Float },
    { name: 'gradingText', type: () => String },
    { name: 'maxScore', type: () => Float },
    { name: 'boxX', type: () => Float },
    { name: 'boxY', type: () => Float },
    { name: 'boxWidth', type: () => Float },
    { name: 'boxHeight', type: () => Float },
    { name: 'boxPageNumber', type: () => Float },
    { name: 'addBoundingBox', type: () => Boolean },
    { name: 'deleteBoundingBox', type: () => Boolean },
    { name: 'isApproved', type: () => Boolean },
    { name: 'boxApproved', type: () => Boolean },
    { name: 'reviewerFeedback', type: () => String }, // NEU: Zweitkorrektor-Feedback
    { name: 'rerunOcr', type: () => Boolean }, // Force re-running OCR for a box even if coordinates are unchanged
  ],

  resolve:
    (
      submissionId: string,
      mainTaskId: string,
      subTaskId?: string | null,
      boundingBoxIndex = 0,
      extractedText?: string,
      score?: number,
      gradingText?: string,
      maxScore?: number,
      boxX?: number,
      boxY?: number,
      boxWidth?: number,
      boxHeight?: number,
      boxPageNumber?: number,
      addBoundingBox?: boolean,
      deleteBoundingBox?: boolean,
      isApproved?: boolean,
      boxApproved?: boolean,
      reviewerFeedback?: string, // NEU: Als Argument empfangen
      rerunOcr?: boolean,
    ) =>
    async (_gqlFields: BaseQLSelectionSet<UpdateSubmissionTaskResult>) => {
      // Only the submission's owner or the owner of its exam may edit it (missing id == denied, not `success: false`).
      await loadAndAssert(Submission, submissionId, 'update', { _id: 1 });
      const submissionCollection = await getCollection(Submission);

      // Fetch the submission to find the correct array indices
      const submission = (await submissionCollection.findOne({ _id: submissionId })) as any;
      if (!submission) {
        return new UpdateSubmissionTaskResult({
          success: false,
          message: `Submission with ID ${submissionId} not found.`,
        });
      }

      // Find the main task index
      const mainTaskIndex = submission.taskSubmissions?.findIndex(
        (task: any) => String(task._id) === String(mainTaskId),
      );
      if (mainTaskIndex === -1 || mainTaskIndex === undefined) {
        logger.error(
          {
            mainTaskId,
            availableIds: submission.taskSubmissions?.map((t: any) => String(t._id)),
            context: 'updateSubmissionTask',
          },
          'Main task not found',
        );
        return new UpdateSubmissionTaskResult({
          success: false,
          message: `Main task with ID ${mainTaskId} not found.`,
        });
      }

      const isMainTask = !subTaskId || subTaskId === '' || subTaskId === 'null' || subTaskId === 'undefined';
      const hasBoxCoordinates =
        typeof boxX === 'number' &&
        typeof boxY === 'number' &&
        typeof boxWidth === 'number' &&
        typeof boxHeight === 'number' &&
        typeof boxPageNumber === 'number';

      const normalizedPageNumber = typeof boxPageNumber === 'number' ? Math.max(0, boxPageNumber) : 0;
      const topLeftX = typeof boxX === 'number' ? Math.max(0, boxX * 10) : 0;
      const topLeftY = typeof boxY === 'number' ? Math.max(0, boxY * 10) : 0;
      const bottomRightX =
        typeof boxX === 'number' && typeof boxWidth === 'number' ? Math.max(0, (boxX + boxWidth) * 10) : 0;
      const bottomRightY =
        typeof boxY === 'number' && typeof boxHeight === 'number' ? Math.max(0, (boxY + boxHeight) * 10) : 0;

      const getTargetTask = () => {
        const mainTask = submission.taskSubmissions?.[mainTaskIndex];
        if (!mainTask) {
          return { error: `Main task with ID ${mainTaskId} not found.` } as const;
        }

        if (isMainTask) {
          return {
            task: mainTask,
            pathPrefix: `taskSubmissions.${mainTaskIndex}`,
          } as const;
        }

        const findNestedSubTaskById = (
          tasks: any[] | undefined,
          targetId: string,
          indexPath: number[] = [],
          taskNumberPath: string[] = [],
        ): { task: any; indexPath: number[]; taskNumberPath: string[] } | null => {
          if (!Array.isArray(tasks) || tasks.length === 0) return null;

          for (let i = 0; i < tasks.length; i += 1) {
            const candidate = tasks[i];
            if (!candidate) continue;
            const candidatePath = [...indexPath, i];
            const newNumberPath = [...taskNumberPath, String(candidate.taskNumber || '')];

            if (String(candidate._id) === targetId) {
              return { task: candidate, indexPath: candidatePath, taskNumberPath: newNumberPath };
            }

            const nested = findNestedSubTaskById(candidate.subTasksSubmission, targetId, candidatePath, newNumberPath);
            if (nested) return nested;
          }

          return null;
        };

        const resolvedNestedSubTask = findNestedSubTaskById(mainTask.subTasksSubmission, String(subTaskId));
        if (!resolvedNestedSubTask) {
          const collectAllSubTaskIds = (tasks: any[] | undefined): string[] => {
            if (!Array.isArray(tasks) || tasks.length === 0) return [];
            const out: string[] = [];
            for (const task of tasks) {
              if (!task) continue;
              out.push(String(task._id));
              out.push(...collectAllSubTaskIds(task.subTasksSubmission));
            }
            return out;
          };

          logger.error(
            {
              subTaskId,
              availableIds: collectAllSubTaskIds(mainTask.subTasksSubmission),
              context: 'updateSubmissionTask',
            },
            'Sub task not found',
          );
          return { error: `Sub task with ID ${subTaskId} not found.` } as const;
        }

        const nestedPathSegments = resolvedNestedSubTask.indexPath
          .map(index => `subTasksSubmission.${index}`)
          .join('.');

        return {
          task: resolvedNestedSubTask.task,
          pathPrefix: `taskSubmissions.${mainTaskIndex}.${nestedPathSegments}`,
          taskNumberPath: resolvedNestedSubTask.taskNumberPath,
        } as const;
      };

      const targetTaskResult = getTargetTask();
      if ('error' in targetTaskResult) {
        return new UpdateSubmissionTaskResult({ success: false, message: targetTaskResult.error });
      }

      const { task: targetTask, pathPrefix, taskNumberPath } = targetTaskResult as any;
      const workingBoxes = Array.isArray(targetTask?.boundingBoxes) ? [...targetTask.boundingBoxes] : [];
      let didMutateBoundingBoxes = false;
      // How many words changed in the answer text this edit, used to gate the auto-regrade dispatches
      // below against the teacher's cutoffs — see countChangedWords.
      let wordsChangedThisEdit = 0;
      const approvedBoundingBoxIdsToClear = new Set<string>();

      const runOcrForBox = async (pageNumberOneBased: number, x1: number, y1: number, x2: number, y2: number) => {
        const sourceImageId = submission.imageIds?.[Math.max(0, pageNumberOneBased - 1)];
        if (!sourceImageId) {
          // No source image to crop/classify → safe fallback to "both" (never auto-"image").
          return {
            imageId: undefined as string | undefined,
            ocrExtractedText: '',
            confidence: 0,
            contentType: LEGACY_CONTENT_TYPE as BoundingBoxContentType,
          };
        }

        const croppedImage = await cropAndSaveRegion(sourceImageId, { x1, y1, x2, y2 });

        // For task types with forceImage, skip OCR — store the cropped region as image for LLM vision processing.
        const behaviour = taskTypeBehaviours[targetTask.taskType as TaskType];
        if (behaviour?.forceImage) {
          return {
            imageId: croppedImage._id,
            ocrExtractedText: '',
            confidence: 1,
            contentType: 'image' as BoundingBoxContentType,
          };
        }

        const ocrResult = await runOCROnImage(croppedImage._id, OcrExtractionType.STUDENT_ANSWER, targetTask.taskType);
        return {
          imageId: croppedImage._id,
          ocrExtractedText: (ocrResult.answer || '').trim(),
          confidence: Number.isFinite(ocrResult.confidence) ? ocrResult.confidence : 0,
          contentType: ocrResult.contentType,
        };
      };

      // When the teacher manually sets/edits the text, the box must remain text-visible: an
      // "image" box gains text (→ "both"), "both" stays "both", anything else becomes "text".
      const contentTypeForManualText = (
        base: BoundingBoxContentType | undefined,
        text: string | undefined,
      ): BoundingBoxContentType => {
        if (text === undefined || text.trim().length === 0) return normalizeContentType(base);
        const normalized = normalizeContentType(base);
        return normalized === 'image' || normalized === 'both' ? 'both' : 'text';
      };

      if (deleteBoundingBox === true) {
        if (boundingBoxIndex >= 0 && boundingBoxIndex < workingBoxes.length) {
          workingBoxes.splice(boundingBoxIndex, 1);
          didMutateBoundingBoxes = true;
        }
      }

      if (hasBoxCoordinates && addBoundingBox) {
        const ocrPayload = await runOcrForBox(normalizedPageNumber + 1, topLeftX, topLeftY, bottomRightX, bottomRightY);
        const resolvedApproved = extractedText !== undefined;

        workingBoxes.push({
          _class: 'BoundingBox',
          _id: `bbox-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          pageNumber: normalizedPageNumber + 1,
          topLeft: {
            _class: 'Point',
            x: topLeftX,
            y: topLeftY,
          },
          bottomRight: {
            _class: 'Point',
            x: bottomRightX,
            y: bottomRightY,
          },
          imageId: ocrPayload.imageId ?? null,
          ocrExtractedText: extractedText ?? ocrPayload.ocrExtractedText,
          // Only trust confidence "1" when the teacher typed the text themselves; an OCR
          // result keeps its own confidence so a still-wrong extraction stays flagged.
          confidence: extractedText !== undefined ? 1 : ocrPayload.confidence,
          approved: resolvedApproved,
          isHandwritten: false,
          contentType: contentTypeForManualText(ocrPayload.contentType, extractedText),
        });
        if (resolvedApproved) {
          const addedBoxId = String(workingBoxes[workingBoxes.length - 1]?._id ?? '');
          if (addedBoxId) approvedBoundingBoxIdsToClear.add(addedBoxId);
        }
        didMutateBoundingBoxes = true;
      } else if (hasBoxCoordinates && boundingBoxIndex >= 0 && boundingBoxIndex < workingBoxes.length) {
        const existingBox = workingBoxes[boundingBoxIndex] || {};
        const existingPageNumber = Number(existingBox.pageNumber ?? 0);
        const existingTopLeftX = Number(existingBox?.topLeft?.x ?? 0);
        const existingTopLeftY = Number(existingBox?.topLeft?.y ?? 0);
        const existingBottomRightX = Number(existingBox?.bottomRight?.x ?? 0);
        const existingBottomRightY = Number(existingBox?.bottomRight?.y ?? 0);
        const hasCoordinateChanges =
          existingPageNumber !== normalizedPageNumber + 1 ||
          existingTopLeftX !== topLeftX ||
          existingTopLeftY !== topLeftY ||
          existingBottomRightX !== bottomRightX ||
          existingBottomRightY !== bottomRightY;
        const hasTextChanges = extractedText !== undefined && (existingBox.ocrExtractedText ?? '') !== extractedText;
        if (hasTextChanges) {
          wordsChangedThisEdit = countChangedWords(existingBox.ocrExtractedText ?? '', extractedText!);
        }

        if (hasCoordinateChanges || hasTextChanges || rerunOcr === true) {
          const shouldRerunOcr = hasCoordinateChanges || rerunOcr === true;
          let ocrPayload: {
            imageId: string | undefined;
            ocrExtractedText: string;
            confidence: number;
            contentType: BoundingBoxContentType | undefined;
          };
          if (shouldRerunOcr) {
            const ocrRerunningPath = `${pathPrefix}.boundingBoxes.${boundingBoxIndex}.ocrRerunning`;
            const pageNumberPath = `${pathPrefix}.boundingBoxes.${boundingBoxIndex}.pageNumber`;
            const topLeftPath = `${pathPrefix}.boundingBoxes.${boundingBoxIndex}.topLeft`;
            const bottomRightPath = `${pathPrefix}.boundingBoxes.${boundingBoxIndex}.bottomRight`;
            // Durable "in flight" write, ahead of the OCR call — see BoundingBox.ocrRerunning's doc
            // comment. Any client watching this submission (this tab, a reload, a canvas reopened
            // later) sees it the moment it's set, not only a tab that happened to be open when the
            // request started.
            //
            // The new COORDINATES land in this same write, not just ocrRerunning — they're already
            // final the moment a drag/resize is saved; only the OCR text/confidence genuinely need to
            // wait for the re-read. Leaving them out here left the server's authoritative position
            // stale for the whole OCR round trip, so any subscription push during that window (even one
            // caused by this very ocrRerunning flip) snapped the box back to its pre-drag position,
            // correcting itself only once the final write below landed.
            await submissionCollection.updateOne(
              { _id: submissionId },
              {
                $set: {
                  [ocrRerunningPath]: true,
                  [pageNumberPath]: normalizedPageNumber + 1,
                  [topLeftPath]: { _class: 'Point', x: topLeftX, y: topLeftY },
                  [bottomRightPath]: { _class: 'Point', x: bottomRightX, y: bottomRightY },
                },
              },
            );
            try {
              ocrPayload = await runOcrForBox(normalizedPageNumber + 1, topLeftX, topLeftY, bottomRightX, bottomRightY);
            } catch (err) {
              // Clear it back out on failure too — nothing further in this resolver runs to do it via
              // the normal path below, and a stuck `true` would spin forever for every future viewer.
              await submissionCollection
                .updateOne({ _id: submissionId }, { $set: { [ocrRerunningPath]: false } })
                .catch(clearErr =>
                  logger.error(
                    { err: clearErr, submissionId, boundingBoxIndex, context: 'updateSubmissionTask' },
                    'Failed to clear ocrRerunning after a failed OCR re-run',
                  ),
                );
              throw err;
            }
          } else {
            ocrPayload = {
              imageId: existingBox.imageId ?? undefined,
              ocrExtractedText: (existingBox.ocrExtractedText ?? '').trim(),
              confidence: Number.isFinite(existingBox.confidence) ? existingBox.confidence : 0,
              contentType: existingBox.contentType as BoundingBoxContentType | undefined,
            };
          }
          const resolvedApproved = extractedText !== undefined ? true : (existingBox.approved ?? false);
          if (resolvedApproved === true && existingBox.approved !== true) {
            const existingBoxId = String(existingBox?._id ?? '');
            if (existingBoxId) approvedBoundingBoxIdsToClear.add(existingBoxId);
          }

          workingBoxes[boundingBoxIndex] = {
            ...existingBox,
            pageNumber: normalizedPageNumber + 1,
            topLeft: {
              _class: 'Point',
              x: topLeftX,
              y: topLeftY,
            },
            bottomRight: {
              _class: 'Point',
              x: bottomRightX,
              y: bottomRightY,
            },
            imageId: ocrPayload.imageId ?? existingBox.imageId ?? null,
            ocrExtractedText: extractedText ?? ocrPayload.ocrExtractedText,
            // Manual text edits are trusted (confidence 1); re-OCR keeps its own confidence
            // so a re-extraction that is still wrong stays flagged for review.
            confidence: extractedText !== undefined ? 1 : ocrPayload.confidence,
            approved: resolvedApproved,
            contentType: contentTypeForManualText(ocrPayload.contentType, extractedText),
            // Cleared here too (not just on the failure path above) — this object lands in the SAME
            // combined write as the fresh OCR result, so the "done" state and the fresh text/confidence
            // reach every subscriber together, in one push. Relying on the early write's `true` to be
            // separately overwritten later would leave a window where the result is already visible but
            // the flag still reads "running".
            ocrRerunning: false,
          };
          didMutateBoundingBoxes = true;
        }
      } else if (extractedText !== undefined && boundingBoxIndex >= 0 && boundingBoxIndex < workingBoxes.length) {
        if ((workingBoxes[boundingBoxIndex]?.ocrExtractedText ?? '') !== extractedText) {
          wordsChangedThisEdit = countChangedWords(
            workingBoxes[boundingBoxIndex]?.ocrExtractedText ?? '',
            extractedText,
          );
          const existingApproved = workingBoxes[boundingBoxIndex]?.approved === true;
          workingBoxes[boundingBoxIndex] = {
            ...workingBoxes[boundingBoxIndex],
            ocrExtractedText: extractedText,
            confidence: 1,
            approved: true,
            contentType: contentTypeForManualText(workingBoxes[boundingBoxIndex]?.contentType, extractedText),
          };
          if (!existingApproved) {
            const editedBoxId = String(workingBoxes[boundingBoxIndex]?._id ?? '');
            if (editedBoxId) approvedBoundingBoxIdsToClear.add(editedBoxId);
          }
          didMutateBoundingBoxes = true;
        }
      } else if (boxApproved !== undefined && boundingBoxIndex >= 0 && boundingBoxIndex < workingBoxes.length) {
        if ((workingBoxes[boundingBoxIndex]?.approved ?? false) !== boxApproved) {
          workingBoxes[boundingBoxIndex] = {
            ...workingBoxes[boundingBoxIndex],
            approved: boxApproved,
          };
          if (boxApproved) {
            const approvedBoxId = String(workingBoxes[boundingBoxIndex]?._id ?? '');
            if (approvedBoxId) approvedBoundingBoxIdsToClear.add(approvedBoxId);
          }
          didMutateBoundingBoxes = true;
        }
      }

      if (isApproved === true) {
        for (let i = 0; i < workingBoxes.length; i++) {
          if (workingBoxes[i]?.approved !== true) {
            const boxIdToApprove = String(workingBoxes[i]?._id ?? '');
            workingBoxes[i] = { ...workingBoxes[i], approved: true };
            if (boxIdToApprove) approvedBoundingBoxIdsToClear.add(boxIdToApprove);
            didMutateBoundingBoxes = true;
          }
        }
      }

      const updateFields: any = {};
      const shouldRebuildStudentAnswer =
        didMutateBoundingBoxes &&
        (deleteBoundingBox === true || addBoundingBox === true || hasBoxCoordinates || extractedText !== undefined);

      // WICHTIG: Überschreibt die Boxen nur, wenn auch wirklich eine Aktion stattgefunden hat
      if (didMutateBoundingBoxes) {
        updateFields[`${pathPrefix}.boundingBoxes`] = workingBoxes;
      }
      if (shouldRebuildStudentAnswer) {
        updateFields[`${pathPrefix}.studentAnswer`] = aggregateOCRFromBoundingBoxes(workingBoxes as any);
      }

      // Baue das Update-Objekt für die Datenbank
      // NOTE: Avoid using dot-notation to set nested gradingResult fields
      // because gradingResult may be explicitly null in some documents.
      // Instead, construct the full gradingResult object and $set it atomically.
      const shouldUpdateGradingResult =
        score !== undefined ||
        gradingText !== undefined ||
        maxScore !== undefined ||
        isApproved !== undefined ||
        reviewerFeedback !== undefined;

      // Hoisted out of the `if` below so the manual-grading-insight trigger further down (which needs the
      // PRE-write score to detect a genuine change) can read it without recomputing.
      const existingGrading: any = targetTask?.gradingResult || {};

      if (shouldUpdateGradingResult) {
        const newGrading: any = { ...existingGrading };
        if (score !== undefined) {
          newGrading.score = score;
          newGrading.actualPointsGiven = score;
        }
        if (gradingText !== undefined) newGrading.explanation = gradingText;
        if (score !== undefined || gradingText !== undefined) newGrading.gradingSource = 'manual';
        if (maxScore !== undefined) newGrading.maxPoints = maxScore;
        if (isApproved !== undefined) newGrading.isApproved = isApproved;
        if (reviewerFeedback !== undefined) newGrading.reviewerFeedback = reviewerFeedback;

        updateFields[`${pathPrefix}.gradingResult`] = newGrading;
      }

      // Check, ob überhaupt etwas geschrieben werden muss
      const hasAnyMutation = Object.keys(updateFields).length > 0;

      if (!hasAnyMutation) {
        return new UpdateSubmissionTaskResult({
          success: true,
          message: 'Nothing to update.',
        });
      } else {
        const result = await submissionCollection.updateOne({ _id: submissionId }, { $set: updateFields });

        if (result.matchedCount === 0) {
          return new UpdateSubmissionTaskResult({
            success: false,
            message: `Submission with ID ${submissionId} not found during update.`,
          });
        }

        const taskType = isMainTask ? 'main task' : 'subtask';
        logger.info({ taskType, submissionId, mainTaskId, subTaskId }, 'Updated submission task');

        const mainTaskTaskNumber = String(submission.taskSubmissions?.[mainTaskIndex]?.taskNumber || '').trim();
        const specificTaskRef =
          !isMainTask && Array.isArray(taskNumberPath) && taskNumberPath.length > 0
            ? joinTaskRefSegments([mainTaskTaskNumber, ...(taskNumberPath as string[])])
            : mainTaskTaskNumber;
        const canTriggerRegrading =
          shouldRebuildStudentAnswer && Boolean(submission.examId) && specificTaskRef.length > 0;

        const examCollection = await (await import('@adornis/baseql/server/collections.js')).getCollection(Exam);

        if (submission.examId && approvedBoundingBoxIdsToClear.size > 0) {
          const approvedBoxIds = Array.from(approvedBoundingBoxIdsToClear);
          await examCollection
            .updateOne(
              { _id: submission.examId } as any,
              {
                $pull: {
                  'examState.answerExtractionProgress.lowConfRegions': {
                    boundingBoxId: { $in: approvedBoxIds },
                  },
                },
              } as any,
            )
            .catch(error => {
              logger.error(
                { err: error, examId: submission.examId, context: 'updateSubmissionTask' },
                'Failed to sync approved bounding boxes to lowConfRegions',
              );
            });
        }

        const examDoc = await examCollection.findOne(
          { _id: submission.examId },
          {
            projection: { examState: 1, languageUseSchemeRules: 1, userID: 1 },
          },
        );
        const gradingStarted = !!examDoc?.examState?.hasGradingStarted;

        const shouldTriggerRegrading = canTriggerRegrading && gradingStarted;

        // Both cutoffs below gate ONLY the "answer text changed" case (wordsChangedThisEdit > 0) — a pure
        // bounding-box move/resize/add/delete with no text change has nothing to count words on, so it
        // keeps triggering the content regrade exactly as it always has, unaffected by either cutoff.
        let newContentWordTotal = Number(targetTask?.pendingContentWordDelta) || 0;
        let newLanguageUseWordTotal = Number(targetTask?.pendingLanguageUseWordDelta) || 0;
        let contentCutoffCrossed = true;
        let languageUseCutoffCrossed = false;
        let languageUseSchemesToRegrade: any[] = [];

        if (shouldTriggerRegrading && wordsChangedThisEdit > 0) {
          newContentWordTotal += wordsChangedThisEdit;
          newLanguageUseWordTotal += wordsChangedThisEdit;

          const teacherCollection = await getCollection(Teacher);
          const teacher = examDoc?.userID
            ? await teacherCollection.findOne({ userID: examDoc.userID }, { projection: { grammarPreferences: 1 } })
            : null;
          const contentCutoff = Number(teacher?.grammarPreferences?.contentRegradeWordCutoff ?? 0);
          const languageUseCutoff = Number(teacher?.grammarPreferences?.languageUseRegradeWordCutoff ?? 5);

          contentCutoffCrossed = newContentWordTotal >= contentCutoff;
          if (contentCutoffCrossed) newContentWordTotal = 0;

          // dependsOn entries are pipe-joined qualified paths (e.g. "2|a") — distinct from specificTaskRef
          // above, which is built with NO separator for content taskRefs matching and would never match here.
          const qualifiedTaskRef =
            !isMainTask && Array.isArray(taskNumberPath) && taskNumberPath.length > 0
              ? [mainTaskTaskNumber, ...(taskNumberPath as string[])].join('|')
              : mainTaskTaskNumber;
          languageUseSchemesToRegrade = findLanguageUseSchemesForTask(
            qualifiedTaskRef,
            (examDoc?.languageUseSchemeRules as any[]) ?? [],
          );
          languageUseCutoffCrossed =
            languageUseSchemesToRegrade.length > 0 && newLanguageUseWordTotal >= languageUseCutoff;
          if (languageUseCutoffCrossed) newLanguageUseWordTotal = 0;
        }

        if (shouldTriggerRegrading && contentCutoffCrossed) {
          // ONE submission, ONE task ref — both scopes travel in the job payload. Without them the job would
          // grade this task for the whole cohort, which is not what correcting one student's answer means.
          // `prepareRegradingRun` is the job's now, so a requeue re-pends the right nodes.
          //
          // Fire-and-forget, same as the languageUse dispatch below and for the same reason: this is an
          // auxiliary trigger, not the save the teacher (or, for a bbox edit, the canvas's loading state) is
          // waiting on. It used to be `dispatchJobsAndWait`, which made this mutation's promise resolve only
          // once the regrade finished — several seconds after the OCR/text write it's actually meant to
          // represent had already landed and been pushed to the client. Nothing reads this dispatch's outcome
          // (errors were already swallowed below), and the regrade's own progress is independently visible via
          // the `grading` job row it creates (see getLiveGradingJobsQuery) — so nothing is lost by not waiting.
          const { dispatchGradingWave } = await import('./jobs/dispatch-grading.js');
          void dispatchGradingWave(String(submission.examId), {
            taskRefs: [specificTaskRef],
            submissionIds: [submissionId],
          }).catch(error => {
            logger.error(
              { err: error, submissionId, task: specificTaskRef, context: 'updateSubmissionTask' },
              'Regrading failed',
            );
          });
        }

        if (languageUseCutoffCrossed) {
          // Same job type/scope as the content dispatch above, one row per matched scheme — a task can
          // belong to more than one LanguageUseScheme. Fire-and-forget: this is an auxiliary trigger, not
          // the save the teacher is waiting on. Never includeSoloLanguageUse — that flag is reserved for
          // the exam's genuine first-ever grading run, not an edit-triggered regrade (see grading-agent.tool.ts).
          const { dispatchGradingWave } = await import('./jobs/dispatch-grading.js');
          for (const scheme of languageUseSchemesToRegrade) {
            void dispatchGradingWave(String(submission.examId), {
              taskRefs: Array.isArray(scheme?.dependsOn) ? scheme.dependsOn : [],
              submissionIds: [submissionId],
              target: 'languageUse',
              languageUseSchemeId: String(scheme?._id ?? ''),
            }).catch(error => {
              logger.error(
                { err: error, submissionId, schemeId: scheme?._id, context: 'updateSubmissionTask' },
                'Language-use regrade failed',
              );
            });
          }
        }

        if (shouldTriggerRegrading && wordsChangedThisEdit > 0) {
          await submissionCollection.updateOne(
            { _id: submissionId },
            {
              $set: {
                [`${pathPrefix}.pendingContentWordDelta`]: newContentWordTotal,
                [`${pathPrefix}.pendingLanguageUseWordDelta`]: newLanguageUseWordTotal,
              },
            },
          );
        }

        // A manual score correction is a grading "insight" — fold it into the shared rubric and re-grade
        // every OTHER submission of this task against it (see manual-grading-insight.ts), including ones
        // already isApproved. Gated on a genuine point change (not a mere isApproved toggle or wording
        // edit) so approving/re-wording feedback never fires a rubric refinement + exam-wide regrade.
        const priorScore = typeof existingGrading.score === 'number' ? existingGrading.score : undefined;
        if (
          gradingStarted &&
          specificTaskRef.length > 0 &&
          submission.examId &&
          score !== undefined &&
          priorScore !== undefined &&
          Math.abs(score - priorScore) > 1000 // 0.001
        ) {
          const { captureManualGradingInsightAndRegrade } = await import('./pipeline/manual-grading-insight.js');
          void captureManualGradingInsightAndRegrade({
            examId: String(submission.examId),
            submissionId,
            taskRef: specificTaskRef,
            mainTaskNumber: mainTaskTaskNumber,
            oldScore: priorScore,
            newScore: score,
            maxPoints:
              typeof (existingGrading as any).maxPoints === 'number' ? (existingGrading as any).maxPoints : maxScore,
            oldExplanation: String(existingGrading.explanation ?? ''),
            newExplanation: gradingText,
            studentAnswerExcerpt: String(targetTask?.studentAnswer ?? '').slice(0, 500),
          }).catch(error => {
            logger.error(
              { err: error, submissionId, task: specificTaskRef, context: 'updateSubmissionTask' },
              'Manual grading insight capture failed',
            );
          });
        }

        return new UpdateSubmissionTaskResult({
          success: true,
          message: 'Update successful.',
        });
      }
    },
});

/**
 * Teacher override of a multi-task LanguageUseScheme group's shared result (Submission.languageUseRuleResults),
 * keyed by submissionId + languageUseSchemeId rather than a single task — there is no single task to
 * address, since the result is shared across every task the group's dependsOn spans. No regrade
 * cascade: this is a terminal manual override, same as isApproved/score edits in
 * updateSubmissionTaskMutation above.
 */
export const updateLanguageUseRuleResultMutation = registerMutation({
  operationName: 'updateLanguageUseRuleResultMutation',
  type: () => Boolean,
  params: [
    { name: 'submissionId', type: () => String },
    { name: 'languageUseSchemeId', type: () => String },
    { name: 'score', type: () => Float },
    { name: 'explanation', type: () => String },
    { name: 'isApproved', type: () => Boolean },
    { name: 'actualPointsGiven', type: () => Float },
    { name: 'reviewerFeedback', type: () => String },
  ],
  resolve:
    (
      submissionId: string,
      languageUseSchemeId: string,
      score?: number,
      explanation?: string,
      isApproved?: boolean,
      actualPointsGiven?: number,
      reviewerFeedback?: string,
    ) =>
    async () => {
      // The helper writes raw by id (it is shared with the grading jobs), so the client caller is checked here.
      await loadAndAssert(Submission, submissionId, 'update', { _id: 1 });
      const { upsertLanguageUseRuleResult } = await import('./language-use-rule-result.js');
      await upsertLanguageUseRuleResult(submissionId, languageUseSchemeId, {
        score,
        explanation,
        isApproved,
        actualPointsGiven,
        reviewerFeedback,
      });
      return true;
    },
});
